/* eslint-disable no-magic-numbers */
import { Injectable } from '@angular/core';
import { WorkOverviewEntry } from '../enums/work-overview-entry.enum';
import { CellColoringStrategy } from '../models/cell-coloring-strategy.type';
import { IUnitComplianceCell } from '../models/work-overview-details.model';

const STANDARD_HALF_HOUR_THRESHOLD = 24;

type EntryFamily = 'work' | 'rest';
type UnitComplianceColorSource = Pick<IUnitComplianceCell, 'unitComplianceColor'>;

@Injectable({ providedIn: 'root' })
export class WorkOverviewColoringService {
  isWork(type: WorkOverviewEntry): boolean {
    return type === WorkOverviewEntry.Work || type === WorkOverviewEntry.AdditionalWork;
  }

  isRest(type: WorkOverviewEntry): boolean {
    return type === WorkOverviewEntry.Rest || type === WorkOverviewEntry.AdditionalRest;
  }

  getDisplayCellType({
    cellType,
    coloringStrategy,
    rowUnitCompliances,
    scheduleIndex,
  }: {
    cellType: WorkOverviewEntry;
    coloringStrategy: CellColoringStrategy;
    rowUnitCompliances: UnitComplianceColorSource[];
    scheduleIndex: number;
  }): WorkOverviewEntry {
    if (coloringStrategy === 'classicManual' || cellType === WorkOverviewEntry.None) {
      return cellType;
    }

    if (this.isWork(cellType)) {
      return this.isBeyondThresholdForEntryFamily({
        rowUnitCompliances,
        scheduleIndex,
        entryFamily: 'work',
      })
        ? WorkOverviewEntry.AdditionalWork
        : WorkOverviewEntry.Work;
    }

    if (this.isRest(cellType)) {
      return this.isBeyondThresholdForEntryFamily({
        rowUnitCompliances,
        scheduleIndex,
        entryFamily: 'rest',
      })
        ? WorkOverviewEntry.AdditionalRest
        : WorkOverviewEntry.Rest;
    }

    return cellType;
  }

  private isBeyondThresholdForEntryFamily({
    rowUnitCompliances,
    scheduleIndex,
    entryFamily,
  }: {
    rowUnitCompliances: UnitComplianceColorSource[];
    scheduleIndex: number;
    entryFamily: EntryFamily;
  }): boolean {
    let matchingHalfHours = 0;

    for (let i = 0; i <= scheduleIndex; i++) {
      const type = rowUnitCompliances[i]?.unitComplianceColor ?? WorkOverviewEntry.None;
      const matchesEntryFamily = entryFamily === 'work' ? this.isWork(type) : this.isRest(type);

      if (matchesEntryFamily) {
        matchingHalfHours++;
      }
    }

    return matchingHalfHours > STANDARD_HALF_HOUR_THRESHOLD;
  }
}
